import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { NewsCard } from '@/components/base/NewsCard';
import { categories, newsArticles } from '@/mocks/news';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryNews = newsArticles.filter((a) => a.category === slug);
  const otherNews = newsArticles.filter((a) => a.category !== slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background-50 pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">分类不存在</h1>
            <p className="text-foreground-600">请返回首页浏览其他内容</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.meiridasaiheiliaowang.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category.name,
        item: `https://www.meiridasaiheiliaowang.com/category/${category.slug}`,
      },
    ],
  };

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 每日大赛黑料网`,
    description: category.description,
    url: `https://www.meiridasaiheiliaowang.com/category/${category.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryNews.map((news, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://www.meiridasaiheiliaowang.com/article/${news.id}`,
        name: news.title,
      })),
    },
  };

  return (
    <>
      <Navbar />
      <main>
        <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
          <div className="absolute inset-0 bg-background-100"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6 pt-16">
              <div className="max-w-3xl">
                <div className="w-12 h-12 flex items-center justify-center text-accent-500 mb-4">
                  <i className={`${category.icon} text-3xl`}></i>
                </div>
                <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight">
                  {category.name}
                  <br />
                  <span className="text-secondary-500">热点追踪</span>
                </h1>
                <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-lg">{category.description}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-10 md:py-16">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: category.name },
              ]}
            />

            <div className="flex flex-col gap-4 mt-6">
              {categoryNews.length > 0 ? (
                categoryNews.map((news) => (
                  <NewsCard
                    key={news.id}
                    id={news.id}
                    title={news.title}
                    excerpt={news.excerpt}
                    imageUrl={news.imageUrl}
                    author={news.author}
                    publishDate={news.publishDate}
                    readCount={news.readCount}
                    tags={news.tags}
                    variant="horizontal"
                  />
                ))
              ) : (
                <p className="text-foreground-600 py-12 text-center">该分类暂无内容</p>
              )}
            </div>
          </div>
        </section>

        {otherNews.length > 0 && (
          <section className="bg-background-100 py-10 md:py-16">
            <div className="max-w-[1440px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950 mb-8">
                其他热门爆料
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {otherNews.slice(0, 3).map((news) => (
                  <NewsCard
                    key={news.id}
                    id={news.id}
                    title={news.title}
                    excerpt={news.excerpt}
                    imageUrl={news.imageUrl}
                    author={news.author}
                    publishDate={news.publishDate}
                    readCount={news.readCount}
                    tags={news.tags}
                    variant="vertical"
                    size="small"
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionSchema) }} />
    </>
  );
}